import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import AuthService from '../../services/AuthService';
import LeadService from '../../services/LeadService';
import { FaUserAlt } from 'react-icons/fa';
import ToastService from '../../services/ToastService';
import RegisterUserService from '../../services/RegisterUserService';

const PAGE_SIZE = 25;
const EMPLOYEE_ROLE_ID = 4;

function formatDisplayDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function FilterLeadsDashboard() {
  const { fromDateParam, toDateParam } = useParams();
  const navigate = useNavigate();
  const clientId = AuthService.getLoggedInClientId();
  const userRole = AuthService.getLoggedInRoleId();

  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [recordsTotal, setRecordsTotal] = useState(0);

  const [selectedValue, setSelectedValue] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [isEmployeeSelectedError, setIsEmployeeSelectedError] = useState(false);

  const toast = useRef(null);
  const toastService = useMemo(() => new ToastService(toast), []);
  const canBulkAssignLeads = parseInt(userRole, 10) !== 4;

  const totalPages = Math.max(1, Math.ceil(recordsTotal / PAGE_SIZE));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch, fromDateParam, toDateParam, clientId]);

  const loadLeads = useCallback(async () => {
    if (!clientId || !fromDateParam || !toDateParam) {
      setLeads([]);
      setRecordsTotal(0);
      return;
    }
    setLoading(true);
    try {
      const result = await LeadService.getFilteredLeadsByClientId(
        clientId,
        fromDateParam,
        toDateParam,
        pageNumber,
        PAGE_SIZE,
        debouncedSearch
      );
      setLeads(result.data || []);
      setRecordsTotal(result.recordsTotal || 0);
    } catch (error) {
      console.error('Error fetching filtered leads:', error);
      toastService.showError('Error', 'Failed to fetch leads for your workspace');
      setLeads([]);
      setRecordsTotal(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, fromDateParam, toDateParam, pageNumber, debouncedSearch]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    async function fetchEmployees() {
      if (!clientId) return;
      try {
        const users = await RegisterUserService.getUsersByClientId(clientId);
        const employeeList = (users || []).filter(
          (u) => parseInt(u.userRoleId ?? u.UserRoleId, 10) === EMPLOYEE_ROLE_ID
        );
        setEmployees(employeeList);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    }
    fetchEmployees();
  }, [clientId]);

  const handleChange = (event) => {
    setSelectedValue(event);
    if (event) setIsEmployeeSelectedError(false);
  };

  const handleCheckboxChange = (leadId) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const AssignLead = async () => {
    setIsEmployeeSelectedError(!selectedValue);
    if (selectedRows.size === 0) {
      toastService.showError('Error', 'Please select at least one lead');
      return;
    }
    if (!selectedValue) {
      toastService.showError('Validation Error', 'Please select an employee.');
      return;
    }
    try {
      await LeadService.AssignedLeadsToEmployee(selectedValue, Array.from(selectedRows));
      toastService.showSuccess('Success', 'Leads assigned successfully');
      setSelectedValue('');
      setSelectedRows(new Set());
      loadLeads();
    } catch (error) {
      toastService.showError('Error', 'Failed to assign leads');
    }
  };

  const displayFrom = formatDisplayDate(fromDateParam);
  const displayTo = formatDisplayDate(toDateParam);
  const rangeLabel = `${displayFrom} — ${displayTo}`;

  return (
    <div style={{ padding: '20px', background: '#f4f6fb', minHeight: '100vh' }}>
      <Toast ref={toast} />
      <style>{`
        .fld-row:hover{background:#f8f9fe!important}
        .fld-assignbtn:hover{opacity:.88;transform:translateY(-1px)}
        .fld-editbtn:hover{background:#344767!important;color:#fff!important}
        .fld-check{width:16px;height:16px;accent-color:#344767;cursor:pointer}
        .fld-check:disabled{opacity:.35;cursor:not-allowed}
        .fld-pagebtn:disabled{opacity:.45;cursor:not-allowed}
        .fld-pagebtn:not(:disabled):hover{background:#f0f2ff!important}
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg,#344767,#21243d)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '18px', boxShadow: '0 4px 14px rgba(52,71,103,0.3)' }}>
            <i className="fas fa-filter" />
          </div>
          <div>
            <h5 style={{ margin: 0, fontWeight: '700', color: '#344767', fontSize: '18px' }}>Filtered Leads</h5>
            <p style={{ margin: 0, fontSize: '12px', color: '#8392ab' }}>
              <i className="fas fa-calendar-alt" style={{ marginRight: '5px' }} />
              {rangeLabel}
              <span style={{ margin: '0 8px' }}>·</span>
              <i className="fas fa-building" style={{ marginRight: '5px' }} />
              Your workspace only
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{ background: '#fff', border: '1.5px solid #e9ecef', borderRadius: '9px', padding: '8px 18px', color: '#344767', fontWeight: '600', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 6px rgba(52,71,103,0.07)' }}
        >
          <i className="fas fa-arrow-left" /> Back
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', padding: '14px 18px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#8392ab', fontSize: '13px' }} />
            <input
              type="text"
              placeholder="Search name, phone, email, code, source…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: '34px', padding: '9px 12px 9px 34px', border: '1.5px solid #e9ecef', borderRadius: '9px', fontSize: '13px', color: '#344767', width: '100%', outline: 'none' }}
            />
          </div>

          <span style={{ background: '#f0f2ff', color: '#5e72e4', borderRadius: '20px', padding: '5px 14px', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>
            {recordsTotal} Lead{recordsTotal !== 1 ? 's' : ''}
          </span>

          {canBulkAssignLeads && (
            <>
              <select
                value={selectedValue}
                onChange={(e) => handleChange(e.target.value)}
                style={{
                  padding: '9px 12px', border: `1.5px solid ${isEmployeeSelectedError ? '#f5365c' : '#e9ecef'}`,
                  borderRadius: '9px', fontSize: '13px', color: '#344767', background: '#fff',
                  outline: 'none', minWidth: '200px', cursor: 'pointer',
                }}
              >
                <option value="" disabled>Select employee to assign…</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
              {selectedRows.size > 0 && (
                <span style={{ background: '#344767', color: '#fff', borderRadius: '20px', padding: '5px 14px', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                  {selectedRows.size} selected
                </span>
              )}
              <button
                className="fld-assignbtn"
                onClick={AssignLead}
                style={{ background: 'linear-gradient(135deg,#344767,#21243d)', border: 'none', borderRadius: '9px', padding: '9px 20px', color: '#fff', fontWeight: '600', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', boxShadow: '0 4px 12px rgba(52,71,103,0.25)', transition: 'all .2s', whiteSpace: 'nowrap' }}
              >
                <FaUserAlt size={12} /> Assign Leads
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: '14px', boxShadow: '0 2px 12px rgba(52,71,103,0.07)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '48px 20px', textAlign: 'center', color: '#8392ab', fontWeight: '600' }}>
            <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
            Loading leads…
          </div>
        ) : leads.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#f4f6fb', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: '24px', color: '#c0c8d8' }}>
              <i className="fas fa-inbox" />
            </div>
            <p style={{ color: '#8392ab', fontWeight: '600', margin: 0 }}>No leads found for this workspace and date range</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fe', borderBottom: '2px solid #e9ecef' }}>
                    {canBulkAssignLeads && (
                      <th style={{ padding: '12px 16px', width: '48px' }} />
                    )}
                    {['Lead Code', 'Name', 'Mobile', 'Email', 'Source', 'Property', 'Start Date', 'Status', ''].map((h) => (
                      <th key={h || 'actions'} style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '700', color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', textAlign: h === '' ? 'center' : 'left' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, idx) => (
                    <tr
                      key={lead.id}
                      className="fld-row"
                      style={{ borderBottom: '1px solid #f0f2f5', background: idx % 2 === 0 ? '#fff' : '#fafbff', transition: 'background .15s' }}
                    >
                      {canBulkAssignLeads && (
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            className="fld-check"
                            disabled={lead.isLeadAssigned}
                            checked={selectedRows.has(lead.id)}
                            onChange={() => handleCheckboxChange(lead.id)}
                          />
                        </td>
                      )}
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: '#f0f2ff', color: '#5e72e4', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', fontWeight: '700' }}>
                          {lead.leadCode || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: '600', color: '#344767', fontSize: '13px', whiteSpace: 'nowrap' }}>
                        {lead.firstName} {lead.lastName || ''}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#344767' }}>{lead.contactNumber || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#525f7f', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.email || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '12px', color: '#525f7f' }}>{lead.source || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '12px', color: '#525f7f', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.property || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '12px', color: '#8392ab', whiteSpace: 'nowrap' }}>
                        {lead.startDate ? new Date(lead.startDate).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          background: lead.isLeadAssigned ? '#e8faf3' : '#fff4e5',
                          color: lead.isLeadAssigned ? '#2dce89' : '#fb6340',
                          borderRadius: '6px', padding: '3px 8px', fontSize: '11px', fontWeight: '700',
                        }}>
                          {lead.isLeadAssigned ? 'Assigned' : 'Open'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <button
                          className="fld-editbtn"
                          onClick={() => navigate(`/editlead/${lead.id}`)}
                          style={{ background: 'transparent', border: '1.5px solid #344767', borderRadius: '8px', padding: '6px 12px', color: '#344767', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px', transition: 'all .2s' }}
                        >
                          <i className="fas fa-edit" /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', padding: '14px 18px', borderTop: '1px solid #f0f2f5' }}>
              <span style={{ fontSize: '12px', color: '#8392ab', fontWeight: '600' }}>
                Showing {(pageNumber - 1) * PAGE_SIZE + 1}–{Math.min(pageNumber * PAGE_SIZE, recordsTotal)} of {recordsTotal}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  className="fld-pagebtn"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  style={{ background: '#fff', border: '1.5px solid #e9ecef', borderRadius: '8px', padding: '7px 12px', fontSize: '12px', fontWeight: '600', color: '#344767', cursor: 'pointer' }}
                >
                  <i className="fas fa-chevron-left" /> Prev
                </button>
                <span style={{ fontSize: '12px', color: '#344767', fontWeight: '700', padding: '0 8px' }}>
                  Page {pageNumber} of {totalPages}
                </span>
                <button
                  className="fld-pagebtn"
                  disabled={pageNumber >= totalPages}
                  onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                  style={{ background: '#fff', border: '1.5px solid #e9ecef', borderRadius: '8px', padding: '7px 12px', fontSize: '12px', fontWeight: '600', color: '#344767', cursor: 'pointer' }}
                >
                  Next <i className="fas fa-chevron-right" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FilterLeadsDashboard;
